import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ApiError } from '@lombok/api-client';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { InvoiceStatus, formatMoney, type Invoice, type OrderItem } from '@lombok/shared';
import { Button, Card, CardContent, CardHeader, CardTitle } from '@lombok/ui';
import { PageHeader } from '@/components/PageHeader';
import { FORM_COLUMN } from '@/components/layout';
import { BackLink, DetailRow } from '@/components/DetailRow';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { StatusPill } from '@/components/StatusPill';
import { QueryState } from '@/components/QueryState';
import { TextareaField } from '@/components/form';
import { useSessionProfile } from '@/auth/useSessionProfile';
import { useAccountNames, useInvoice, useOrder, useVoidInvoice } from '@/data/hooks';
import { api } from '@/lib/api';
import { pricedItems } from '@/lib/orderView';
import { queryKeys } from '@/lib/queryClient';
import { INVOICE_STAFF_ROLES, invoiceKindLabel, invoiceNumberLabel, invoicePdfState } from './labels';

interface FacturaDetailPageProps {
  /** Group root for the "Facturas" back link, e.g. "/app/customer/facturas". */
  basePath: string;
  backLabel?: string;
  /** Group root for the related-order link, e.g. "/app/admin/pedidos" (omit to show the id only). */
  orderBasePath?: string;
}

/**
 * Factura detail: document number + status pill, the money breakdown (total / pagado / saldo,
 * all USD via the shared `formatMoney` on minor units), the lines that were billed, the issue +
 * due dates and the related order (linked when `orderBasePath` is given). Admin and finance can
 * void an invoice that has no payment applied (with a reason). The `customerId` query param
 * identifies the invoice's DynamoDB partition — the list rows pass it in the URL, mirroring the
 * Pagos `?accountId=` pattern.
 */
export function FacturaDetailPage({
  basePath,
  backLabel = 'Facturas',
  orderBasePath,
}: FacturaDetailPageProps) {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const customerId = searchParams.get('customerId') ?? undefined;
  const { profile } = useSessionProfile();
  const isStaff = profile ? INVOICE_STAFF_ROLES.includes(profile.role) : false;
  const query = useInvoice(id, customerId);
  const invoice = query.data;

  return (
    <div className={`flex flex-col gap-6 ${FORM_COLUMN}`}>
      <div className="flex flex-col gap-2">
        <BackLink to={basePath}>{backLabel}</BackLink>
        <PageHeader
          title={invoice ? `${invoiceKindLabel(invoice.kind)} ${invoiceNumberLabel(invoice)}` : 'Factura'}
          description="Documento interno de cobro: no es una factura fiscal."
          actions={
            invoice ? (
              <div className="flex flex-wrap items-center gap-2">
                <StatusPill kind="invoice" value={invoice.status} />
                <PdfAction key={invoice.invoiceId} invoice={invoice} isStaff={isStaff} />
                {isStaff ? <VoidInvoiceAction invoice={invoice} /> : null}
              </div>
            ) : undefined
          }
        />
      </div>

      {!customerId ? (
        <Card className="p-6 text-sm text-text-muted">
          Falta el identificador de la cuenta para abrir esta factura. Vuelve a la lista de
          facturas e intenta de nuevo.
        </Card>
      ) : (
        <QueryState
          isLoading={query.isLoading}
          isError={query.isError}
          error={query.error}
          onRetry={() => void query.refetch()}
          loadingLabel="Cargando factura…"
        >
          {invoice ? (
            <div className="flex flex-col gap-6">
              {invoice.status === InvoiceStatus.VOID ? <VoidNotice invoice={invoice} /> : null}
              <AmountCard invoice={invoice} />
              <LinesCard invoice={invoice} />
              <DetailsCard invoice={invoice} orderBasePath={orderBasePath} showCustomer={isStaff} />
            </div>
          ) : null}
        </QueryState>
      )}
    </div>
  );
}

/** How long the screen waits for the PDF worker before telling the user to try again. */
const PDF_WAIT_MS = 90 * 1000;
const PDF_POLL_MS = 3000;

/**
 * PDF of the document. With a current PDF it is one click to download. While the worker is still
 * making it ("Generando…") the screen re-reads the invoice every few seconds and downloads as soon
 * as it is ready. For documents that never got one, admin/finance see "Generar PDF" (customers get
 * the same request when they press "Descargar PDF"). A failure is shown as a message and can be retried.
 */
function PdfAction({ invoice, isStaff }: { invoice: Invoice; isStaff: boolean }) {
  const qc = useQueryClient();
  const state = invoicePdfState(invoice);
  const [busy, setBusy] = useState(false);
  const [waitingSince, setWaitingSince] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const downloading = useRef(false);

  const generating = state === 'pending' || waitingSince !== null;

  const fetchLink = useCallback(async (): Promise<'ready' | 'generating'> => {
    const result = await api.getInvoicePdf(invoice.invoiceId, invoice.customerId);
    if (result.status === 'ready') {
      const a = document.createElement('a');
      a.href = result.url;
      a.download = result.fileName;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
    return result.status;
  }, [invoice.invoiceId, invoice.customerId]);

  const onClick = async () => {
    setError(null);
    setBusy(true);
    try {
      const status = await fetchLink();
      if (status === 'generating') setWaitingSince(Date.now());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'No se pudo preparar el PDF. Intenta de nuevo.');
    } finally {
      setBusy(false);
    }
  };

  // While generating: re-read the invoice until the PDF shows up (or the wait runs out).
  useEffect(() => {
    if (!generating) return undefined;
    const timer = window.setInterval(() => {
      if (waitingSince !== null && Date.now() - waitingSince > PDF_WAIT_MS) {
        setWaitingSince(null);
        setError('El PDF todavía no está listo. Intenta de nuevo en unos minutos.');
        return;
      }
      void qc.invalidateQueries({ queryKey: queryKeys.invoice(invoice.invoiceId, invoice.customerId) });
    }, PDF_POLL_MS);
    return () => window.clearInterval(timer);
  }, [generating, waitingSince, qc, invoice.invoiceId, invoice.customerId]);

  // The user asked and the PDF just arrived: download it without another click.
  useEffect(() => {
    if (waitingSince === null || state !== 'ready' || downloading.current) return;
    downloading.current = true;
    fetchLink()
      .catch((err: unknown) => setError(err instanceof ApiError ? err.message : 'No se pudo descargar el PDF.'))
      .finally(() => {
        downloading.current = false;
        setWaitingSince(null);
      });
  }, [waitingSince, state, fetchLink]);

  const label = generating ? 'Generando…' : state === 'ready' || !isStaff ? 'Descargar PDF' : 'Generar PDF';

  return (
    <>
      <Button size="sm" variant="outline" onClick={() => void onClick()} disabled={busy || generating}>
        {busy ? 'Preparando…' : label}
      </Button>
      {error ? (
        <span role="alert" className="basis-full text-sm font-medium text-danger">
          {error}
        </span>
      ) : null}
    </>
  );
}

/** "Anular" with a mandatory reason. Only offered while nothing was paid on the invoice. */
function VoidInvoiceAction({ invoice }: { invoice: Invoice }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const voidInvoice = useVoidInvoice(invoice.invoiceId, invoice.customerId);

  const canVoid =
    (invoice.status === InvoiceStatus.ISSUED || invoice.status === InvoiceStatus.OVERDUE) &&
    invoice.paidMinorUsd === 0;
  if (!canVoid) return null;

  return (
    <>
      <Button
        size="sm"
        variant="danger"
        onClick={() => {
          setReason('');
          setOpen(true);
        }}
      >
        Anular
      </Button>
      <ConfirmDialog
        open={open}
        title={`Anular ${invoiceNumberLabel(invoice)}`}
        confirmLabel="Anular factura"
        danger
        confirmDisabled={reason.trim().length === 0}
        onConfirm={() => voidInvoice.mutateAsync(reason.trim())}
        onClose={() => setOpen(false)}
      >
        <div className="flex flex-col gap-3">
          <p>
            La factura queda anulada y su saldo en 0. No se puede deshacer, y no se puede anular
            una factura que ya recibió pagos.
          </p>
          <TextareaField
            label="Motivo"
            required
            maxLength={500}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            name="void-reason"
          />
        </div>
      </ConfirmDialog>
    </>
  );
}

/** Why and when a voided invoice was voided. */
function VoidNotice({ invoice }: { invoice: Invoice }) {
  return (
    <Card className="border-border-strong p-4 text-sm text-text-muted">
      <span className="font-medium text-text-primary">Factura anulada</span>
      {invoice.voidedAt ? ` el ${formatDate(invoice.voidedAt)}` : ''}
      {invoice.voidedByName ? ` por ${invoice.voidedByName}` : ''}
      {invoice.voidReason ? (
        <>
          {' · '}
          <span className="font-medium text-text-primary">Motivo:</span> {invoice.voidReason}
        </>
      ) : null}
    </Card>
  );
}

/** Money panel: total, paid, and the outstanding saldo (all USD). */
function AmountCard({ invoice: inv }: { invoice: Invoice }) {
  return (
    <div className="grid gap-4 sm:grid-cols-3">
      <Card>
        <CardContent className="pt-5">
          <p className="text-sm text-text-muted">Total (USD)</p>
          <p className="mt-1 text-2xl font-semibold tracking-tight text-text-primary">
            {formatMoney(inv.totalMinorUsd)}
          </p>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="pt-5">
          <p className="text-sm text-text-muted">Pagado (USD)</p>
          <p className="mt-1 text-2xl font-semibold tracking-tight text-success">
            {formatMoney(inv.paidMinorUsd)}
          </p>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="pt-5">
          <p className="text-sm text-text-muted">Saldo pendiente (USD)</p>
          <p className="mt-1 text-2xl font-semibold tracking-tight text-text-primary">
            {formatMoney(inv.balanceMinorUsd)}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * What was billed. Invoices issued from now on carry their own copy of the order lines; older
 * ones have none, so the lines are read from the related order instead.
 */
function LinesCard({ invoice: inv }: { invoice: Invoice }) {
  const needsOrder = !inv.items && Boolean(inv.orderId);
  const order = useOrder(needsOrder ? inv.orderId : undefined);
  const items: OrderItem[] = inv.items ?? pricedItems(order.data?.items ?? []);
  if (items.length === 0) return null;

  return (
    <Card className="overflow-hidden">
      <CardHeader>
        <CardTitle>Artículos facturados</CardTitle>
      </CardHeader>
      <CardContent className="px-0 pb-0 pt-0">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-border text-xs uppercase tracking-wide text-text-muted">
                <th className="px-4 py-3 font-medium">Producto</th>
                <th className="px-4 py-3 font-medium">SKU</th>
                <th className="px-4 py-3 text-right font-medium">Cant.</th>
                <th className="px-4 py-3 text-right font-medium">Precio</th>
                <th className="px-4 py-3 text-right font-medium">Total línea</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.lineNumber} className="border-b border-border last:border-0">
                  <td className="px-4 py-3 font-medium text-text-primary">{it.productName}</td>
                  <td className="px-4 py-3 font-mono text-xs text-text-muted">{it.skuCode}</td>
                  <td className="px-4 py-3 text-right text-text-muted">{it.quantity}</td>
                  <td className="px-4 py-3 text-right text-text-muted">
                    {formatMoney(it.unitPriceMinorUsd)}
                  </td>
                  <td className="px-4 py-3 text-right font-medium text-text-primary">
                    {formatMoney(it.lineTotalMinorUsd)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-border">
                <td className="px-4 py-3 font-semibold text-text-primary" colSpan={4}>
                  Total (USD)
                </td>
                <td className="px-4 py-3 text-right text-lg font-semibold text-text-primary">
                  {formatMoney(inv.totalMinorUsd)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

/** Document kind, status, dates, account, and the related order. */
function DetailsCard({
  invoice: inv,
  orderBasePath,
  showCustomer,
}: {
  invoice: Invoice;
  orderBasePath?: string;
  /** Admin/finance see the customer's name; other roles only ever see their own account. */
  showCustomer: boolean;
}) {
  const names = useAccountNames(showCustomer);
  const customerName = names.data?.get(inv.customerId)?.name;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Información de la factura</CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <dl className="flex flex-col">
          <DetailRow label="Número">{invoiceNumberLabel(inv)}</DetailRow>
          <DetailRow label="Tipo de documento">{invoiceKindLabel(inv.kind)}</DetailRow>
          <DetailRow label="Estado">
            <StatusPill kind="invoice" value={inv.status} />
          </DetailRow>
          {showCustomer ? (
            <DetailRow label="Cliente">{customerName ?? (names.isLoading ? '…' : inv.customerId)}</DetailRow>
          ) : null}
          <DetailRow label="Pedido relacionado">
            {inv.orderId ? (
              orderBasePath ? (
                <Link
                  to={`${orderBasePath}/${inv.orderId}`}
                  className="font-mono text-brand-primary hover:underline"
                >
                  {inv.orderNumber ?? inv.orderId}
                </Link>
              ) : (
                <span className="font-mono">{inv.orderNumber ?? inv.orderId}</span>
              )
            ) : (
              '—'
            )}
          </DetailRow>
          <DetailRow label="Fecha de emisión">{formatDate(inv.issueDate)}</DetailRow>
          <DetailRow label="Vence">{formatDate(inv.dueDate)}</DetailRow>
          {inv.exchangeRate ? (
            <DetailRow label="Tasa de cambio">{inv.exchangeRate.rate} VES/USD</DetailRow>
          ) : null}
        </dl>
      </CardContent>
    </Card>
  );
}

/** Compact local date (es-VE); raw value fallback if unparseable. */
function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('es-VE', { day: '2-digit', month: 'short', year: 'numeric' });
}
